<template>
  <div v-if="isDevelopment" class="fixed bottom-4 right-4 z-50">
    <div class="bg-white rounded-lg shadow-xl border-2 border-yellow-400 p-4 max-w-sm">
      <!-- 标题栏 -->
      <div class="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
        <div class="flex items-center gap-2">
          <span class="text-lg">🧪</span>
          <span class="text-sm font-semibold text-gray-700">测试工具</span>
        </div>
        <button
          @click="isExpanded = !isExpanded"
          class="text-gray-500 hover:text-gray-700 transition-colors"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            stroke-width="2" 
            stroke-linecap="round" 
            stroke-linejoin="round"
            :class="{ 'rotate-180': isExpanded }"
            class="transition-transform"
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </button>
      </div>

      <!-- 展开内容 -->
      <div v-if="isExpanded" class="space-y-3">
        <!-- 快速选择评分 -->
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-2">
            快速全部评分
          </label>
          <div class="grid grid-cols-5 gap-2">
            <button
              v-for="rating in [1, 2, 3, 4, 5]"
              :key="rating"
              @click="fillAllWithRating(rating)"
              class="px-3 py-2 text-sm font-medium rounded-md transition-colors bg-gray-100 hover:bg-gray-200 text-gray-700"
            >
              {{ rating }}
            </button>
          </div>
        </div>

        <!-- 选择人格类型 -->
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-2">
            按人格类型匹配答案
          </label>
          <select
            v-model="selectedPersonality"
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400"
          >
            <option value="">-- 选择人格类型 --</option>
            <option
              v-for="personality in personalityList"
              :key="personality"
              :value="personality"
            >
              {{ getPersonalityEmoji(personality) }} {{ personality }}
            </option>
          </select>
          <button
            @click="fillWithPersonality"
            :disabled="!selectedPersonality"
            class="mt-2 w-full px-3 py-2 text-sm font-medium rounded-md transition-colors bg-yellow-400 hover:bg-yellow-500 text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            填充答案
          </button>
        </div>

        <!-- 随机填充 -->
        <div>
          <button
            @click="fillRandom"
            class="w-full px-3 py-2 text-sm font-medium rounded-md transition-colors bg-psychology-primary hover:bg-psychology-primary/90 text-white"
          >
            🎲 随机填充
          </button>
        </div>

        <!-- 快速提交 -->
        <div class="pt-2 border-t border-gray-200">
          <button
            @click="quickSubmit"
            :disabled="answeredCount < 40"
            class="w-full px-3 py-2 text-sm font-medium rounded-md transition-colors bg-psychology-primary hover:bg-psychology-primary/90 text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {{ answeredCount < 40 ? `快速提交 (${answeredCount}/40)` : '快速提交 ✓' }}
          </button>
        </div>

        <!-- 当前状态 -->
        <div class="pt-2 border-t border-gray-200">
          <div class="text-xs text-gray-500 space-y-1">
            <div>已答题: {{ answeredCount }}/40</div>
            <div>当前维度: {{ currentDimension }}/4</div>
            <div>当前题目: {{ currentQuestionInDimension }}/10</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { 
  getAllPersonalityTypes, 
  getPersonalityEmoji,
  generateAnswersForPersonality,
  generateAnswersForRating,
  generateRandomAnswers
} from '@/utils/testHelper'
import { useToast } from 'vue-toastification'

export default {
  name: 'TestQuickFill',
  props: {
    answers: {
      type: Array,
      required: true
    },
    currentDimension: {
      type: Number,
      required: true
    },
    currentQuestionInDimension: {
      type: Number,
      required: true
    }
  },
  emits: ['update-answers', 'submit'],
  data() {
    return {
      isExpanded: true,
      selectedPersonality: '',
      personalityList: []
    }
  },
  computed: {
    isDevelopment() {
      return process.env.NODE_ENV === 'development'
    },
    answeredCount() {
      return this.answers.length
    }
  },
  mounted() {
    this.personalityList = getAllPersonalityTypes()
  },
  methods: {
    getPersonalityEmoji,
    
    /**
     * 快速填充所有答案为指定评分
     */
    fillAllWithRating(rating) {
      try {
        const newAnswers = generateAnswersForRating(rating)
        this.$emit('update-answers', newAnswers)
        
        const toast = useToast()
        toast.success(`已填充所有答案为 ${rating} 分`)
      } catch (error) {
        const toast = useToast()
        toast.error(`填充失败: ${error.message}`)
      }
    },
    
    /**
     * 根据选择的人格类型填充答案
     */
    fillWithPersonality() {
      if (!this.selectedPersonality) {
        const toast = useToast()
        toast.warning('请先选择人格类型')
        return
      }
      
      try {
        const newAnswers = generateAnswersForPersonality(this.selectedPersonality)
        this.$emit('update-answers', newAnswers)
        
        const toast = useToast()
        toast.success(`已根据 ${this.getPersonalityEmoji(this.selectedPersonality)} ${this.selectedPersonality} 填充答案`)
      } catch (error) {
        const toast = useToast()
        toast.error(`填充失败: ${error.message}`)
      }
    },
    
    /**
     * 随机填充答案
     */
    fillRandom() {
      try {
        const newAnswers = generateRandomAnswers()
        this.$emit('update-answers', newAnswers)
        
        const toast = useToast()
        toast.success('已随机填充所有答案')
      } catch (error) {
        const toast = useToast()
        toast.error(`填充失败: ${error.message}`)
      }
    },
    
    /**
     * 快速提交
     */
    quickSubmit() {
      if (this.answeredCount < 40) {
        const toast = useToast()
        toast.warning(`还有 ${40 - this.answeredCount} 道题未作答`)
        return
      }
      
      this.$emit('submit')
    }
  }
}
</script>

<style scoped>
/* 组件样式 */
</style>
